import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import useGetMyAccountTeams from '@/api/queries/me/useGetMyAccountTeams';
import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import { usePermissions } from '@/hooks/usePermissions';
import {
  getCalendarFilterAssignedUserIdsLocalStorageKey,
  getCalendarFilterPipelineIdsLocalStorageKey,
  getCalendarFilterTeamIdsLocalStorageKey,
  getCalendarGroupByTeamLocalStorageKey,
  getCalendarHideNonBusinessHoursLocalStorageKey,
  getCalendarViewLocalStorageKey,
  getScheduleDisplayTypeLocalStorageKey,
  getScheduleRangeUnitStorageKey,
} from '@/helpers/localStorageKeyHelper';
import { Props as TScheduledEventFieldsetProps } from '@/ui/components/scheduledEventFieldset/ScheduledEventFieldset';
import { PipelineType } from '@/gql/graphql';
import FullCalendar from '@fullcalendar/react';
import { pruneStaleTeamIds } from '@payaca/components/calendar/UserResourceCalendar';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { DispatchPermissions } from '@payaca/permissions/dispatch/dispatch.permissions';
import { PipelinesPermissions } from '@payaca/permissions/pipelines/pipelines.permissions';
import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import { requestGetListedScheduledEvents } from '@payaca/store/scheduledEvents/scheduledEventsActions';
import { CreateScheduledEventRequestData } from '@payaca/store/scheduledEvents/scheduledEventsTypes';
import { Address } from '@payaca/types/locationTypes';
import { ScheduledEvent } from '@payaca/types/scheduledEventsTypes';
import { User } from '@payaca/types/userTypes';
import { assertUnreachable, isNotNullish } from '@payaca/utilities/guards';
import { DeepPartial, Nullish } from '@payaca/utilities/types';
import {
  setHours,
  setMinutes,
  setSeconds,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
} from 'date-fns';
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { UNASSIGNED_NUMERIC_SENTINEL } from '../accountUserAvatarSelector/AccountUserAvatarSelector';
import { CreateScheduledEventModal } from '../createScheduledEventModal/CreateScheduledEventModal';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import ScheduledEventReadDrawer from '../scheduledEventReadDrawer/ScheduledEventReadDrawer';

const displayTypeStorageKey = getScheduleDisplayTypeLocalStorageKey();
const rangeUnitStorageKey = getScheduleRangeUnitStorageKey();
const calendarViewStorageKey = getCalendarViewLocalStorageKey();
const calendarHideNonBusinessHoursStorageKey =
  getCalendarHideNonBusinessHoursLocalStorageKey();
const calendarFilterAssignedUserIdsStorageKey =
  getCalendarFilterAssignedUserIdsLocalStorageKey();
const calendarGroupByTeamStorageKey = getCalendarGroupByTeamLocalStorageKey();
const calendarFilterTeamIdsStorageKey =
  getCalendarFilterTeamIdsLocalStorageKey();
const calendarFilterPipelineIdsStorageKey =
  getCalendarFilterPipelineIdsLocalStorageKey();

export const NO_PIPELINE = 'no_pipeline';

export const pruneUserIdsToTeamMembers = ({
  userIds,
  teamIds,
  accountUsers,
}: {
  userIds: number[];
  teamIds: string[];
  accountUsers: { id: string; teams: { items: { id: string }[] } }[];
}): number[] => {
  const memberIds = new Set(
    accountUsers
      .filter((user) =>
        user.teams.items.some((team) => teamIds.includes(team.id))
      )
      .map((user) => Number(user.id))
  );
  return userIds.filter(
    (id) => id === UNASSIGNED_NUMERIC_SENTINEL || memberIds.has(id)
  );
};

export const SCHEDULE_PIPELINE_TYPES: PipelineType[] = ['STANDARD', 'SERVICE'];

export type CalendarType = 'grid' | 'userResource';
export type DisplayType = 'map' | 'calendar';
export type ScheduleRangeUnit = 'day' | 'week' | 'month';

type onUpdateProps = Partial<{
  selectedUserIds: User['id'][];
  selectedTeamIds: string[];
  selectedPipelineIds: string[];
  startAt: Date;
  endAt: Date;
  scheduleRangeUnit: ScheduleRangeUnit;
  displayType: DisplayType;
  excludeNonBusinessHours: boolean;
  groupByTeam: boolean;
  calendarType: CalendarType;
  location: Nullish<
    Pick<
      Address,
      'line1' | 'line2' | 'city' | 'administrativeArea' | 'country' | 'postcode'
    >
  >;
}>;

const SCHEDULE_PAGE_SIZE = 5000;

export type EventToCreate = DeepPartial<CreateScheduledEventRequestData>;

type ScheduleContextType = {
  requestData: {
    startAt?: Date;
    endAt?: Date;
    assignedUserIds?: User['id'][];
    projectPipelineIds?: string[];
  };
  displayType: DisplayType;
  scheduleRangeUnit: ScheduleRangeUnit;
  calendarViewData: {
    excludeNonBusinessHours: boolean;
    groupByTeam: boolean;
    // Client-side team filter. Kept off `requestData` so changing it does not
    // trigger the scheduled-events refetch (team filtering only hides rows).
    selectedTeamIds: string[];
    calendarType: CalendarType;
  };
  mapViewData: {
    location?: Nullish<
      Pick<
        Address,
        | 'line1'
        | 'line2'
        | 'city'
        | 'administrativeArea'
        | 'country'
        | 'postcode'
      >
    >;
  };
  onUpdate: (props: onUpdateProps) => void;

  isPipelineFilterEnabled: boolean;

  hideCreateEventButton?: boolean;

  scheduledEvents?: ScheduledEvent[];
  // Patch a scheduled event in place (e.g. optimistically after a drag/drop or
  // resize) so re-renders don't revert the calendar to stale data.
  applyScheduledEventPatch: (
    scheduledEventId: number,
    patch: Partial<
      Pick<ScheduledEvent, 'beginAt' | 'endAt' | 'userAssignments'>
    >
  ) => void;

  pendingEventToCreate?: EventToCreate;
  setPendingEventToCreate: (eventToCreate?: EventToCreate) => void;

  calendarRef?: React.RefObject<FullCalendar | null>;

  focusedDate: Date;
  setFocusedDate: (date: Date) => void;
  listenerSubscribe: (
    type: TListenerType,
    identifier: string,
    func: () => void
  ) => void;
  listenerUnsubscribe: (type: TListenerType, identifier: string) => void;
};

export const ScheduleContext = React.createContext<ScheduleContextType>({
  requestData: {},
  displayType: 'calendar',
  scheduleRangeUnit: 'week',
  calendarViewData: {
    excludeNonBusinessHours: false,
    groupByTeam: false,
    selectedTeamIds: [],
    calendarType: 'grid',
  },
  mapViewData: {},
  onUpdate: () => null,
  isPipelineFilterEnabled: false,
  applyScheduledEventPatch: () => null,
  setPendingEventToCreate: () => null,
  focusedDate: new Date(),
  setFocusedDate: () => null,
  listenerSubscribe: () => null,
  listenerUnsubscribe: () => null,
});

type TListenerType = 'onEventCreated' | 'onEventUpdated' | 'onEventDeleted';

export type TScheduleContextProviderLocationState =
  | {
      eventToCreate?: EventToCreate;
    }
  | undefined;

const ScheduleContextProvider: FC<
  PropsWithChildren<{
    allowEventCreation?: boolean;
    enablePipelineFilter?: boolean;
    hideCreateEventButton?: boolean;
    createEventBase?: EventToCreate;
    disabledFields?: TScheduledEventFieldsetProps['disabledFields'];
    hiddenFields?: TScheduledEventFieldsetProps['hiddenFields'];
  }>
> = ({
  children,
  createEventBase,
  hideCreateEventButton = false,
  allowEventCreation = true,
  enablePipelineFilter = false,
  disabledFields,
  hiddenFields,
}) => {
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const { state } = useLocation<TScheduleContextProviderLocationState>();
  const isCreatingOrEditingEvent = useRouteMatch<{
    eventId: string;
  }>({
    path: `${path}/:eventId`,
    exact: true,
  });

  const { userHasRequiredPermission } = usePermissions();
  const { data: activeUsers } = useGetMyAccountUsersExcludedDeactivated();
  const canGetTeams = userHasRequiredPermission([
    AccountsPermissions.GET_TEAMS,
  ]);
  const { data: accountTeams, isPending: isPendingAccountTeams } =
    useGetMyAccountTeams({ enabled: canGetTeams });
  const activeAccountUserIds = useMemo(() => {
    return activeUsers ? activeUsers.map((u) => +u.id) : [];
  }, [activeUsers]);

  const isPipelineFilterEnabled =
    enablePipelineFilter &&
    userHasRequiredPermission([PipelinesPermissions.GET_PIPELINES_FOR_ACCOUNT]);
  const { data: pipelines } = useGetMyAccountPipelines({
    types: SCHEDULE_PIPELINE_TYPES,
    enabled: isPipelineFilterEnabled,
  });

  const filterValidSelectedPipelineIds = (pipelineIds: string[]) => {
    return pipelineIds.filter(
      (id) => id === NO_PIPELINE || pipelines?.some((p) => p.publicId === id)
    );
  };

  // oxlint-disable-next-line typescript/no-redundant-type-constituents -- FIXME: pre-existing error: -1 is overridden by number in this union type.
  const filterValidSelectedUserIds = (userIds: (-1 | User['id'])[]) => {
    return userIds.filter(
      (id) => id === -1 || activeAccountUserIds.includes(id) // "unassigned" selected or userId must be for an active account user
    );
  };

  const listeners = useRef<Record<TListenerType, Record<string, () => void>>>({
    onEventCreated: {},
    onEventUpdated: {},
    onEventDeleted: {},
  });

  const listenerSubscribe = useCallback(
    (type: TListenerType, identifier: string, func: () => void) => {
      listeners.current[type][identifier] = func;
    },
    []
  );

  const listenerUnsubscribe = useCallback(
    (type: TListenerType, identifier: string) => {
      delete listeners.current[type][identifier];
    },
    []
  );

  const listenerTrigger = useCallback((type: TListenerType) => {
    Object.values(listeners.current[type]).forEach((func) => func());
  }, []);

  const initialUserAssignments = userHasRequiredPermission([
    ScheduledEventsPermissions.GET_EVENTS,
  ])
    ? filterValidSelectedUserIds(
        localStorage
          .getItem(calendarFilterAssignedUserIdsStorageKey)
          ?.split(',')
          .map((x) => (x === 'unassigned' ? -1 : +x))
          .filter((x) => !!x) || []
      )
    : [];

  const initialPipelineIds = isPipelineFilterEnabled
    ? filterValidSelectedPipelineIds(
        localStorage
          .getItem(calendarFilterPipelineIdsStorageKey)
          ?.split(',')
          .filter(Boolean) || []
      )
    : [];

  const initialExcludeNonBusinessHours: boolean =
    localStorage.getItem(calendarHideNonBusinessHoursStorageKey) === 'true';

  // Default to grouped-by-team; only off when the user has explicitly turned it
  // off. Grouping has no visible effect until the account has teams (gated in
  // Schedule.tsx), so this is safe for teamless accounts.
  const initialGroupByTeam: boolean =
    localStorage.getItem(calendarGroupByTeamStorageKey) !== 'false';

  const initialSelectedTeamIds: string[] =
    localStorage
      .getItem(calendarFilterTeamIdsStorageKey)
      ?.split(',')
      .filter(Boolean) || [];

  const initialCalendarType: CalendarType =
    localStorage.getItem(calendarViewStorageKey) === 'userResource' &&
    userHasRequiredPermission([
      ScheduledEventsPermissions.GET_EVENTS,
      ScheduledEventsPermissions.GET_MY_DEAL_EVENTS,
    ])
      ? 'userResource'
      : 'grid';

  const initialDisplayType: DisplayType =
    localStorage.getItem(displayTypeStorageKey) === 'map' &&
    userHasRequiredPermission([DispatchPermissions.GET_EVENTS])
      ? 'map'
      : 'calendar';

  const initialRangeUnit: ScheduleRangeUnit = localStorage.getItem(
    rangeUnitStorageKey
  )?.length
    ? (localStorage.getItem(rangeUnitStorageKey) as ScheduleRangeUnit)
    : 'week';

  const dispatch = useDispatch();
  const isMountedRef = useRef(false);
  const latestListRequestIdRef = useRef(0);

  const [hasSetInitialAssignedUsers, setHasSetInitialAssignedUsers] =
    useState<boolean>(false);
  const [hasSetInitialPipelineIds, setHasSetInitialPipelineIds] =
    useState<boolean>(!!pipelines);
  const [focusedDate, setFocusedDate] = useState(new Date());

  const [scheduledEvents, setScheduledEvents] = useState<ScheduledEvent[]>();
  const [pendingEventToCreate, setPendingEventToCreate] =
    useState<EventToCreate>();
  const calendarRef = useRef<FullCalendar>(null);
  const [scheduleRangeUnit, setScheduleRangeUnit] =
    useState<ScheduleRangeUnit>(initialRangeUnit);
  const [displayType, setDisplayType] =
    useState<DisplayType>(initialDisplayType);
  const [requestData, setRequestData] = useState<
    ScheduleContextType['requestData']
  >({
    assignedUserIds: initialUserAssignments,
    projectPipelineIds: initialPipelineIds,
  });
  const [calendarViewData, setCalendarViewdata] = useState<
    ScheduleContextType['calendarViewData']
  >({
    excludeNonBusinessHours: initialExcludeNonBusinessHours,
    groupByTeam: initialGroupByTeam,
    selectedTeamIds: initialSelectedTeamIds,
    calendarType: initialCalendarType,
  });
  const [mapViewData, setMapViewData] = useState<
    ScheduleContextType['mapViewData']
  >({
    location: undefined,
  });

  const applyScheduledEventPatch = useCallback(
    (
      scheduledEventId: number,
      patch: Partial<
        Pick<ScheduledEvent, 'beginAt' | 'endAt' | 'userAssignments'>
      >
    ) => {
      setScheduledEvents((events) =>
        events?.map((event) =>
          event.id === scheduledEventId ? { ...event, ...patch } : event
        )
      );
    },
    []
  );

  const getListedScheduledEvents = () => {
    if (!requestData.startAt || !requestData.endAt) return;

    // The saga runs every request to completion, so an earlier filter's
    // response can land after a later one's.
    const requestId = ++latestListRequestIdRef.current;
    dispatch(
      requestGetListedScheduledEvents(
        {
          pageNumber: 1,
          pageSize: SCHEDULE_PAGE_SIZE,
          start: requestData.startAt.toISOString(),
          end: requestData.endAt.toISOString(),
          userAssignments: requestData.assignedUserIds?.map((x) =>
            x === -1 ? 'unassigned' : x
          ),
          projectPipelineIds: requestData.projectPipelineIds,
        },
        (scheduledEvents) => {
          if (
            isMountedRef.current &&
            requestId === latestListRequestIdRef.current
          ) {
            setScheduledEvents(scheduledEvents);
          }
        }
      )
    );
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    getListedScheduledEvents();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'getListedScheduledEvents'
  }, [requestData]);

  useEffect(() => {
    if (!hasSetInitialAssignedUsers && activeUsers?.length) {
      if (canGetTeams && isPendingAccountTeams) return;
      // on page reload, activeUsers will not be initially loaded so we cannot filter active users in assignedUserIds
      // this will set request data with initialUsersAssignments in this case, after accounts users exist
      const savedTeamIds = accountTeams
        ? pruneStaleTeamIds(initialSelectedTeamIds, accountTeams)
        : [];
      const assignedUserIds = savedTeamIds.length
        ? pruneUserIdsToTeamMembers({
            userIds: initialUserAssignments,
            teamIds: savedTeamIds,
            accountUsers: activeUsers,
          })
        : initialUserAssignments;
      if (assignedUserIds.length !== initialUserAssignments.length) {
        localStorage.setItem(
          calendarFilterAssignedUserIdsStorageKey,
          assignedUserIds.join(',')
        );
      }
      setRequestData((x) => ({ ...x, assignedUserIds }));
      setHasSetInitialAssignedUsers(true);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'initialUserAssignments'
  }, [
    hasSetInitialAssignedUsers,
    activeUsers,
    accountTeams,
    isPendingAccountTeams,
  ]);

  useEffect(() => {
    if (!hasSetInitialPipelineIds && pipelines) {
      setRequestData((x) => ({ ...x, projectPipelineIds: initialPipelineIds }));
      setHasSetInitialPipelineIds(true);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- initialPipelineIds is derived from pipelines
  }, [hasSetInitialPipelineIds, pipelines]);

  const onUpdate = ({
    selectedUserIds,
    selectedTeamIds,
    selectedPipelineIds,
    startAt,
    endAt,
    scheduleRangeUnit,
    displayType,
    excludeNonBusinessHours,
    groupByTeam,
    calendarType,
    location,
  }: onUpdateProps) => {
    const requestDataUpdates: Partial<ScheduleContextType['requestData']> = {};

    if (isNotNullish(selectedUserIds)) {
      // Filter out any users that are not active
      const activeSelectedUserIds = filterValidSelectedUserIds(selectedUserIds);
      requestDataUpdates.assignedUserIds = activeSelectedUserIds;
      localStorage.setItem(
        calendarFilterAssignedUserIdsStorageKey,
        activeSelectedUserIds.join(',')
      );
    }

    if (isNotNullish(selectedPipelineIds)) {
      const validSelectedPipelineIds =
        filterValidSelectedPipelineIds(selectedPipelineIds);
      requestDataUpdates.projectPipelineIds = validSelectedPipelineIds;
      localStorage.setItem(
        calendarFilterPipelineIdsStorageKey,
        validSelectedPipelineIds.join(',')
      );
    }

    if (isNotNullish(startAt)) {
      requestDataUpdates.startAt = startAt;
    }

    if (isNotNullish(endAt)) {
      requestDataUpdates.endAt = endAt;
    }

    // Only update (and thereby refetch) when a request field actually changed,
    // so client-side-only updates (e.g. team filter) don't trigger a refetch.
    if (Object.keys(requestDataUpdates).length > 0) {
      setRequestData((x) => ({ ...x, ...requestDataUpdates }));
    }

    if (isNotNullish(scheduleRangeUnit)) {
      setScheduleRangeUnit(scheduleRangeUnit);
      localStorage.setItem(rangeUnitStorageKey, scheduleRangeUnit);
    }

    if (isNotNullish(displayType)) {
      setDisplayType(displayType);
      localStorage.setItem(displayTypeStorageKey, displayType);
    }

    const calendarViewDataUpdates: Partial<
      ScheduleContextType['calendarViewData']
    > = {};

    if (isNotNullish(excludeNonBusinessHours)) {
      calendarViewDataUpdates.excludeNonBusinessHours = excludeNonBusinessHours;
      localStorage.setItem(
        calendarHideNonBusinessHoursStorageKey,
        `${excludeNonBusinessHours}`
      );
    }

    if (isNotNullish(groupByTeam)) {
      calendarViewDataUpdates.groupByTeam = groupByTeam;
      localStorage.setItem(calendarGroupByTeamStorageKey, `${groupByTeam}`);
    }

    if (isNotNullish(selectedTeamIds)) {
      calendarViewDataUpdates.selectedTeamIds = selectedTeamIds;
      localStorage.setItem(
        calendarFilterTeamIdsStorageKey,
        selectedTeamIds.join(',')
      );
    }

    if (isNotNullish(calendarType)) {
      calendarViewDataUpdates.calendarType = calendarType;
      localStorage.setItem(calendarViewStorageKey, calendarType);
    }

    setCalendarViewdata((x) => ({ ...x, ...calendarViewDataUpdates }));

    if (!!location || location === null) {
      setMapViewData({ location: location || undefined });
    }
  };

  const beginAt = useMemo(() => {
    let beginAt: Date | undefined;

    if (focusedDate) {
      let startAt: Date = new Date();
      switch (scheduleRangeUnit) {
        case 'day':
          startAt = startOfDay(focusedDate);
          break;
        case 'week':
          startAt = startOfISOWeek(focusedDate);
          break;
        case 'month':
          startAt = startOfMonth(focusedDate);
          break;
        default:
          assertUnreachable(scheduleRangeUnit);
      }

      if (startAt > new Date()) {
        beginAt = setSeconds(setMinutes(setHours(startAt, 8), 0), 0);
      }
    }

    return beginAt;
  }, [scheduleRangeUnit, focusedDate]);

  return (
    <ScheduleContext.Provider
      value={{
        requestData: requestData,
        displayType,
        scheduleRangeUnit,
        calendarViewData,
        mapViewData,
        onUpdate,
        isPipelineFilterEnabled,
        scheduledEvents,
        applyScheduledEventPatch,
        hideCreateEventButton,
        calendarRef,
        pendingEventToCreate,
        setPendingEventToCreate: (eventToCreate) => {
          if (eventToCreate === undefined) {
            setPendingEventToCreate(undefined);
          } else {
            setPendingEventToCreate({ ...createEventBase, ...eventToCreate });
          }
        },
        focusedDate,
        setFocusedDate,
        listenerSubscribe,
        listenerUnsubscribe,
      }}
    >
      {children}
      {allowEventCreation && (
        <PermissionGuard
          renderIfHasPermissions={[
            ScheduledEventsPermissions.ADD_EVENT,
            ScheduledEventsPermissions.ADD_MY_EVENT,
            ScheduledEventsPermissions.ADD_MY_DEAL_EVENT,
          ]}
        >
          <CreateScheduledEventModal
            onClose={() => {
              history.push(url);
            }}
            isOpen={
              !!isCreatingOrEditingEvent &&
              isCreatingOrEditingEvent.params.eventId === 'new'
            }
            initialEventData={(() => {
              const merged = {
                beginAt,
                ...createEventBase,
                ...state?.eventToCreate,
                ...(calendarViewData.calendarType === 'grid'
                  ? { userAssignments: requestData.assignedUserIds }
                  : {}),
              };
              return {
                ...merged,
                userAssignments: merged.userAssignments?.filter(
                  (id) => id !== -1
                ),
              };
            })()}
            onSuccess={(createdScheduledEventId) => {
              getListedScheduledEvents();
              listenerTrigger('onEventCreated');
              history.push(`${url}/${createdScheduledEventId}`);
            }}
            disabledFields={
              !disabledFields &&
              (createEventBase?.dealId || state?.eventToCreate?.dealId)
                ? ['dealId']
                : disabledFields || []
            }
            hiddenFields={hiddenFields}
          />
        </PermissionGuard>
      )}
      <ScheduledEventReadDrawer
        onUpdateTasksSuccess={getListedScheduledEvents}
        onUpdateScheduledEventSuccess={() => {
          getListedScheduledEvents();
          listenerTrigger('onEventUpdated');
        }}
        onDeleteScheduledEventSuccess={() => {
          getListedScheduledEvents();
          listenerTrigger('onEventDeleted');
        }}
        scheduledEventId={
          isCreatingOrEditingEvent
            ? parseInt(isCreatingOrEditingEvent.params.eventId)
            : undefined
        }
        isOpen={
          !!isCreatingOrEditingEvent &&
          isCreatingOrEditingEvent.params.eventId !== 'new'
        }
        onClose={() => {
          history.replace(url);
        }}
      />
    </ScheduleContext.Provider>
  );
};

export default ScheduleContextProvider;
